import * as React from 'react';
import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer';
import { cn } from 'utils/cn';
import { useOverlayScope } from 'components/ui/overlay-motion';

type DrawerContextProps = {
    hasSnapPoints: boolean;
    modal: DrawerPrimitive.Root.Props['modal'];
    showSwipeHandle: boolean;
    swipeDirection: NonNullable<DrawerPrimitive.Root.Props['swipeDirection']>;
};

const DrawerContext = React.createContext<DrawerContextProps | null>(null);

function useDrawer() {
    const context = React.useContext(DrawerContext);

    if (!context) {
        throw new Error('useDrawer must be used within a Drawer.');
    }

    return context;
}

function Drawer({
    modal = true,
    showSwipeHandle = false,
    snapPoints,
    swipeDirection = 'down',
    ...props
}: DrawerPrimitive.Root.Props & {
    showSwipeHandle?: boolean;
}) {
    const hasSnapPoints = snapPoints != null && snapPoints.length > 0;
    const contextValue = React.useMemo(
        () => ({ hasSnapPoints, modal, showSwipeHandle, swipeDirection }),
        [hasSnapPoints, modal, showSwipeHandle, swipeDirection]
    );

    return (
        <DrawerContext.Provider value={contextValue}>
            <DrawerPrimitive.Root
                data-slot="drawer"
                modal={modal}
                snapPoints={snapPoints}
                swipeDirection={swipeDirection}
                {...props}
            />
        </DrawerContext.Provider>
    );
}

function DrawerTrigger({ className, ...props }: DrawerPrimitive.Trigger.Props) {
    return (
        <DrawerPrimitive.Trigger data-slot="drawer-trigger" className={cn('cursor-pointer', className)} {...props} />
    );
}

function DrawerPortal({ container, ...props }: DrawerPrimitive.Portal.Props) {
    const scope = useOverlayScope();
    return (
        <DrawerPrimitive.Portal
            data-slot="drawer-portal"
            container={container === undefined ? scope.container : container}
            {...props}
        />
    );
}

function DrawerClose({ className, ...props }: DrawerPrimitive.Close.Props) {
    return <DrawerPrimitive.Close data-slot="drawer-close" className={cn('cursor-pointer', className)} {...props} />;
}

function DrawerOverlay({ className, ...props }: DrawerPrimitive.Backdrop.Props) {
    const { motion } = useOverlayScope();
    return (
        <DrawerPrimitive.Backdrop
            data-slot="drawer-overlay"
            className={cn(
                'new:bg-scrim-soft fixed inset-0 z-(--z-drawer) min-h-dvh bg-black/10 opacity-[max(var(--drawer-overlay-min-opacity,0),calc(1-var(--drawer-swipe-progress)))] transition-opacity duration-450 ease-[cubic-bezier(0.32,0.72,0,1)] select-none data-ending-style:pointer-events-none data-ending-style:opacity-0 data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-snap-points:[--drawer-overlay-min-opacity:0.5] data-starting-style:opacity-0 data-swiping:duration-0 supports-backdrop-filter:backdrop-blur-xs supports-[-webkit-touch-callout:none]:absolute motion-reduce:duration-150 motion-reduce:data-ending-style:duration-150',
                motion.drawerBackdrop,
                className
            )}
            {...props}
        />
    );
}

function DrawerSwipeHandle({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="drawer-swipe-handle"
            aria-hidden="true"
            className={cn(
                'after:bg-muted relative z-10 flex shrink-0 cursor-grab transition-opacity duration-200 group-data-nested-drawer-open/drawer-popup:opacity-0 group-data-nested-drawer-swiping/drawer-popup:opacity-100 group-data-[swipe-axis=x]/drawer-popup:h-full group-data-[swipe-axis=x]/drawer-popup:w-3 group-data-[swipe-axis=x]/drawer-popup:items-center group-data-[swipe-axis=y]/drawer-popup:h-3 group-data-[swipe-axis=y]/drawer-popup:w-full group-data-[swipe-axis=y]/drawer-popup:justify-center group-data-[swipe-direction=down]/drawer-popup:items-end group-data-[swipe-direction=left]/drawer-popup:order-last group-data-[swipe-direction=left]/drawer-popup:justify-start group-data-[swipe-direction=right]/drawer-popup:justify-end group-data-[swipe-direction=up]/drawer-popup:order-last group-data-[swipe-direction=up]/drawer-popup:items-start after:block after:shrink-0 after:rounded-full group-data-[swipe-axis=x]/drawer-popup:after:h-[100px] group-data-[swipe-axis=x]/drawer-popup:after:w-1.5 group-data-[swipe-axis=y]/drawer-popup:after:h-1.5 group-data-[swipe-axis=y]/drawer-popup:after:w-[100px] active:cursor-grabbing',
                className
            )}
            {...props}
        />
    );
}

function DrawerContent({
    className,
    layerClassName,
    children,
    ...props
}: DrawerPrimitive.Popup.Props & {
    /** Moves the backdrop and the viewport, which hold the stacking, off `z-(--z-drawer)` together. */
    layerClassName?: string;
}) {
    const { hasSnapPoints, modal, showSwipeHandle, swipeDirection } = useDrawer();
    const { motion } = useOverlayScope();
    const swipeAxis = swipeDirection === 'down' || swipeDirection === 'up' ? 'y' : 'x';

    return (
        <DrawerPortal data-slot="drawer-portal">
            {modal === true && (
                <DrawerOverlay data-snap-points={hasSnapPoints ? '' : undefined} className={layerClassName} />
            )}
            <DrawerPrimitive.Viewport
                data-slot="drawer-viewport"
                data-modal={modal}
                className={cn(
                    'pointer-events-none fixed inset-0 z-(--z-drawer) select-none data-[modal=true]:pointer-events-auto',
                    layerClassName
                )}
            >
                <DrawerPrimitive.Popup
                    data-slot="drawer-popup"
                    data-swipe-axis={swipeAxis}
                    data-snap-points={hasSnapPoints ? '' : undefined}
                    className={cn(
                        // Base.
                        'group/drawer-popup bg-popover text-popover-foreground pointer-events-auto fixed z-(--z-drawer) m-(--drawer-inset,0px) flex h-(--drawer-content-height) max-h-(--drawer-content-max-height,none) min-h-0 w-(--drawer-content-width,auto) transform-[translate3d(var(--translate-x,0px),var(--translate-y,0px),0)_scale(var(--stack-scale))] flex-col text-sm transition-[transform,height,opacity,filter] duration-450 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform outline-none select-none [interpolate-size:allow-keywords] data-[swipe-direction=down]:rounded-t-xl data-[swipe-direction=down]:border-t data-[swipe-direction=left]:rounded-r-xl data-[swipe-direction=left]:border-r data-[swipe-direction=right]:rounded-l-xl data-[swipe-direction=right]:border-l data-[swipe-direction=up]:rounded-b-xl data-[swipe-direction=up]:border-b motion-reduce:transition-opacity motion-reduce:duration-150 motion-reduce:ease-out motion-reduce:data-ending-style:transform-none motion-reduce:data-ending-style:opacity-0 motion-reduce:data-starting-style:transform-none motion-reduce:data-starting-style:opacity-0',
                        // Nested.
                        'data-nested-drawer-open:overflow-hidden data-nested-drawer-open:brightness-95',
                        // Bleed.
                        'after:pointer-events-none after:absolute after:bg-(--drawer-bleed-background,var(--color-popover)) data-[swipe-axis=x]:after:inset-y-0 data-[swipe-axis=x]:after:w-(--bleed) data-[swipe-axis=y]:after:inset-x-0 data-[swipe-axis=y]:after:h-(--bleed) data-[swipe-direction=down]:after:top-full data-[swipe-direction=left]:after:right-full data-[swipe-direction=right]:after:left-full data-[swipe-direction=up]:after:bottom-full',
                        // Sizing.
                        '[--drawer-content-height:var(--drawer-height,auto)] data-[swipe-axis=x]:[--drawer-content-width:75%] data-[swipe-axis=y]:[--drawer-content-max-height:calc(100dvh-6rem)] data-[swipe-axis=y]:data-snap-points:[--drawer-content-height:100dvh] data-[swipe-axis=x]:sm:[--drawer-content-width:24rem]',
                        // Stack.
                        '[--bleed:3rem] [--peek:1rem] [--stack-height:var(--drawer-frontmost-height,var(--drawer-height,0px))] [--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--peek)))] [--stack-progress:clamp(0,var(--drawer-swipe-progress),1)] [--stack-scale-base:max(0,calc(1-(var(--nested-drawers)*var(--stack-step))))] [--stack-scale:clamp(0,calc(var(--stack-scale-base)+(var(--stack-step)*var(--stack-progress))),1)] [--stack-shrink:calc(1-var(--stack-scale))] [--stack-step:0.05]',
                        // Transitions. Under reduced motion a drawer let go mid-swipe fades where the finger left it: its
                        // ending transform is the resting one, which still holds the swipe's offset. The 150ms is
                        // important: without it a release fades for the swipe's own duration, up to 400ms.
                        'data-ending-style:transform-(--closed-transform) data-ending-style:opacity-[0.9999] data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-nested-drawer-swiping:duration-0 data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-starting-style:transform-(--closed-transform) data-swiping:duration-0 data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)] motion-reduce:data-ending-style:transform-[translate3d(var(--translate-x,0px),var(--translate-y,0px),0)_scale(var(--stack-scale))] motion-reduce:data-ending-style:duration-150!',
                        // Axis: y.
                        'data-[swipe-axis=y]:inset-x-0 data-[swipe-axis=y]:data-nested-drawer-open:h-(--stack-height)',
                        // Axis: x.
                        'data-[swipe-axis=x]:inset-y-0 data-[swipe-axis=x]:flex-row',
                        // Direction: down.
                        'data-[swipe-direction=down]:bottom-0 data-[swipe-direction=down]:origin-bottom data-[swipe-direction=down]:[--closed-transform:translate3d(0,calc(100%+var(--drawer-inset,0px)+2px),0)] data-[swipe-direction=down]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--stack-shrink)*var(--stack-height)))]',
                        // Direction: up.
                        'data-[swipe-direction=up]:top-0 data-[swipe-direction=up]:origin-top data-[swipe-direction=up]:[--closed-transform:translate3d(0,calc(-100%-var(--drawer-inset,0px)-2px),0)] data-[swipe-direction=up]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)+var(--stack-peek-offset)+(var(--stack-shrink)*var(--stack-height)))]',
                        // Direction: left.
                        'data-[swipe-direction=left]:left-0 data-[swipe-direction=left]:origin-left data-[swipe-direction=left]:[--closed-transform:translate3d(calc(-100%-var(--drawer-inset,0px)-2px),0,0)] data-[swipe-direction=left]:[--translate-x:calc(var(--drawer-swipe-movement-x)+var(--stack-peek-offset)+(var(--stack-shrink)*100%))]',
                        // Direction: right.
                        'data-[swipe-direction=right]:right-0 data-[swipe-direction=right]:origin-right data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+var(--drawer-inset,0px)+2px),0,0)] data-[swipe-direction=right]:[--translate-x:calc(var(--drawer-swipe-movement-x)-var(--stack-peek-offset)-(var(--stack-shrink)*100%))]',
                        motion.drawer,
                        className
                    )}
                    {...props}
                >
                    {showSwipeHandle && <DrawerSwipeHandle />}
                    <DrawerPrimitive.Content
                        data-slot="drawer-content"
                        className={cn(
                            'flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain rounded-[inherit] transition-opacity duration-300 ease-[cubic-bezier(0.45,1.005,0,1.005)] select-text group-data-nested-drawer-open/drawer-popup:opacity-0 group-data-nested-drawer-swiping/drawer-popup:opacity-100 group-data-swiping/drawer-popup:select-none'
                        )}
                    >
                        {children}
                    </DrawerPrimitive.Content>
                </DrawerPrimitive.Popup>
            </DrawerPrimitive.Viewport>
        </DrawerPortal>
    );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="drawer-header"
            className={cn(
                'flex shrink-0 flex-col gap-0.5 p-4 pb-0 group-data-[swipe-axis=y]/drawer-popup:text-center md:gap-1.5 md:text-left',
                className
            )}
            {...props}
        />
    );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="drawer-footer"
            className={cn('mt-auto flex shrink-0 flex-col gap-2 p-4 pt-0', className)}
            {...props}
        />
    );
}

function DrawerTitle({ className, ...props }: DrawerPrimitive.Title.Props) {
    return (
        <DrawerPrimitive.Title
            data-slot="drawer-title"
            className={cn('text-foreground font-medium', className)}
            {...props}
        />
    );
}

function DrawerDescription({ className, ...props }: DrawerPrimitive.Description.Props) {
    return (
        <DrawerPrimitive.Description
            data-slot="drawer-description"
            className={cn('text-muted-foreground text-sm text-balance', className)}
            {...props}
        />
    );
}

export {
    Drawer,
    DrawerPortal,
    DrawerOverlay,
    DrawerSwipeHandle,
    DrawerTrigger,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription,
};
